/**
 * "Edit Description" from the skill page's menu: the line agents read to
 * decide when to use the skill. CaseBlink writes it on save from the title
 * and instructions and keeps it in step with them; typing here makes it the
 * firm's own, and "Let CaseBlink Write It" hands it back. Done changes the
 * page's draft, which the top bar's Save saves with the rest.
 */
import { Sparkles } from "lucide-react";
import { useEffect, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const MAX_DESCRIPTION_CHARS = 1024;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The description in the draft. */
  value: string;
  /** CaseBlink writes it (the draft's `description_auto`). */
  auto: boolean;
  /** Keep this text as the firm's own description. */
  onDone: (description: string) => void;
  /** Let CaseBlink write it again. */
  onReset: () => void;
};

export const EditDescriptionDialog: FC<Props> = ({
  open,
  onOpenChange,
  value,
  auto,
  onDone,
  onReset
}) => {
  const [text, setText] = useState(value);
  useEffect(() => {
    if (open) setText(value);
  }, [open, value]);

  const edited = text !== value;
  const written = auto && !edited;
  const note = written
    ? value.trim()
      ? "Written by CaseBlink. Updates when the instructions change, until you edit it."
      : "CaseBlink writes it when you save, from the title and instructions."
    : !value.trim() && !edited
    ? "CaseBlink writes it when you save. Type your own to use it instead."
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 sm:max-w-lg"
        data-testid="skill-description-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit Description</DialogTitle>
          <DialogDescription>
            Agents read this to decide when to use the automation.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          maxLength={MAX_DESCRIPTION_CHARS}
          placeholder="What it does, then when to use it."
          aria-label="Description"
          data-testid="skill-description-text"
        />
        {note ? (
          <p
            className="flex items-start gap-1.5 text-sm text-fg-3"
            data-testid="skill-description-note"
          >
            <Sparkles className="mt-0.5 size-3.5 shrink-0 text-purple" />
            {note}
          </p>
        ) : null}
        <DialogFooter className="items-center sm:justify-between">
          {auto && !edited ? (
            <span />
          ) : (
            <Button
              variant="link"
              size="sm"
              className="px-0"
              onClick={() => {
                onReset();
                onOpenChange(false);
              }}
              data-testid="skill-description-reset"
            >
              Let CaseBlink Write It
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!edited}
              onClick={() => {
                onDone(text);
                onOpenChange(false);
              }}
              data-testid="skill-description-done"
            >
              Done
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
